import { DocumentNode } from 'graphql';
import { Edit, Star, Users, X } from 'react-feather';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Section } from '../Common/Layout';
import { resizedImageUrl } from '../../common/helpers';
import DateRange from '../../common/Common/DateRange';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useReviewModal, { RegistrationSummary } from './useReviewModal';

export interface ReviewSectionProps {
  summary: RegistrationSummary;
  onClose?: () => unknown;
  refetchQueries?: DocumentNode[];
}

const ReviewSection = ({ summary, onClose, refetchQueries }: ReviewSectionProps) => {
  const { t } = useTranslation();

  return (
    <Section title={t('rate_your_experience')}>
      <ReviewCard summary={summary} onClose={onClose} refetchQueries={refetchQueries} />
    </Section>
  );
};

export interface ReviewCardProps {
  summary: RegistrationSummary;
  onClose?: () => unknown;
  refetchQueries?: DocumentNode[];
}

const ReviewCard = ({ summary, onClose, refetchQueries }: ReviewCardProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();

  const { review } = summary;

  const ratings = [1, 2, 3, 4, 5];
  const [rating, setRating] = useState(summary.review?.rating || 0);

  useEffect(() => {
    setRating(summary.review?.rating || 0);
  }, [summary.review]);

  const [startReview] = useReviewModal({
    summary,
    refetchQueries,
  });

  return (
    <ReviewContainer sc={{ py: 6, px: [4, 6], relative: true }}>
      <UI.Div sc={{ absolute: true, mr: 4, mt: 4 }} style={{ right: 0, top: 0 }}>
        {review && !onClose && (
          <UI.Button
            onClick={() => startReview(review.rating)}
            sc={{ size: 'sm', blank: true, color: 'gray.600', px: 1 }}
            title={t('edit')}
          >
            <UI.Icon>
              <Edit />
            </UI.Icon>
          </UI.Button>
        )}
        {review && onClose && (
          <UI.Button
            onClick={onClose}
            sc={{ size: 'sm', blank: true, color: 'gray.600', px: 1 }}
            title={t('close')}
          >
            <UI.Icon>
              <X />
            </UI.Icon>
          </UI.Button>
        )}
      </UI.Div>
      <UI.FadeIn sc={{ when: !!review }}>
        <UI.FlexContainer
          sc={{
            direction: 'column',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <UI.A
            as={!summary.event.active ? 'span' : undefined}
            href={summary.event.active ? summary.event.checkout_url : undefined}
            target={summary.event.active ? '_blank' : undefined}
            rel={summary.event.active ? 'noopener noreferrer' : undefined}
            sc={{ strong: true, secondary: summary.event.active }}
          >
            <UI.ProfilePic
              sc={{ size: 60, background: summary.event.logo ? 'white' : undefined }}
            >
              {summary.event.logo && (
                <img
                  src={resizedImageUrl(summary.event.logo.url, 300, 'webp')}
                  alt={summary.event.title}
                />
              )}
              {!summary.event.logo && (
                <UI.Span><UI.Span>{summary.event.title.charAt(0)}</UI.Span></UI.Span>
              )}
            </UI.ProfilePic>
          </UI.A>
          <UI.Div sc={{ textAlign: 'center' }}>
            <UI.H4 sc={{ fontSize: 4, mb: 1 }}>
              {review
                ? t('review.thank_you')
                : t('review.name_how_did_it_go', { user: summary.participant.first_name })}
            </UI.H4>
            <UI.Paragraph sc={{ mb: 0 }}>
              {review
                ? t('review.you_reviewed_event', { event: summary.event.title })
                : t('review.rate_your_experience_at_event', { event: summary.event.title })}
            </UI.Paragraph>
          </UI.Div>

          <UI.FlexContainer>
            {ratings.map((value) => (
              <UI.Button
                sc={{
                  brand: 'primary.50',
                  blank: true,
                  size: 'lg',
                  color: value <= rating ? 'primary' : 'gray.800',
                  px: 1.5,
                  py: 1,
                }}
                onMouseEnter={() => setRating(value)}
                onMouseLeave={() => setRating(review?.rating || 0)}
                onFocus={() => setRating(value)}
                onBlur={() => setRating(review?.rating || 0)}
                onClick={() => startReview(value)}
                title={t('review.rating_label', { rating: value })}
                style={{ pointerEvents: !summary.can_add_review ? 'none' : undefined }}
                key={value}
              >
                <UI.Icon sc={{ size: 2, color: value <= rating ? 'primary' : undefined }}>
                  <Star fill={value <= rating ? 'currentColor' : 'none'} strokeWidth={1.5} />
                </UI.Icon>
              </UI.Button>
            ))}
          </UI.FlexContainer>

          <UI.FlexContainer
            sc={{
              wrap: 'wrap', gap: 0.75, alignItems: 'center', justifyContent: 'center', color: 'gray.500', mb: 0,
            }}
          >
            <UI.Label sc={{ small: true }}>
              <DateRange
                start={summary.event.start_date}
                end={summary.event.end_date}
                timezone={summary.event.project.timezone}
              />
            </UI.Label>
            {summary.event.reviews.total > 0 && (
              <>
                <UI.Span>
                  <UI.Icon>
                    <Star />
                  </UI.Icon>
                  {' '}
                  {formatNumber(summary.event.review_average_rating, {
                    minimumFractionDigits: 2, maximumFractionDigits: 2,
                  })}
                </UI.Span>
                <UI.Span>
                  <UI.Icon>
                    <Users />
                  </UI.Icon>
                  {' '}
                  {formatNumber(summary.event.reviews.total)}
                </UI.Span>
              </>
            )}
          </UI.FlexContainer>
        </UI.FlexContainer>
      </UI.FadeIn>
    </ReviewContainer>
  );
};

const ReviewContainer = styled(UI.Div)`
  ${({ theme }) => css`
    background: linear-gradient(10deg, ${theme.colors.primary[25]} 0%, ${theme.colors.white} 100%);
  `}
`;

export default ReviewSection;
